import * as React from 'react';
import { cn, formatCount } from '../lib/utils.js';

/**
 * 画面上部の見出し。パンくず + タイトル + 説明 + 右肩のアクション。
 * 管理コンソール / 契約者パネル双方の全ページで使う。
 */
export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  className,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  breadcrumbs?: Array<{ label: string; href?: string }>;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('mb-6 space-y-2', className)}>
      {breadcrumbs?.length ? (
        <nav className="flex items-center gap-1.5 text-xs text-muted-foreground" aria-label="パンくず">
          {breadcrumbs.map((b, i) => (
            <React.Fragment key={`${b.label}-${i}`}>
              {i > 0 ? <span aria-hidden>/</span> : null}
              {b.href ? (
                <a href={b.href} className="transition-colors hover:text-foreground">
                  {b.label}
                </a>
              ) : (
                <span>{b.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
          {description ? (
            <p className="max-w-3xl text-sm text-muted-foreground">{description}</p>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
    </div>
  );
}

/** カード内の区切りセクション。 */
export function Section({
  title,
  description,
  actions,
  children,
  className,
}: {
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('space-y-3', className)}>
      {title || actions ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="space-y-0.5">
            {title ? <h2 className="text-base font-semibold text-foreground">{title}</h2> : null}
            {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
          </div>
          {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/** ダッシュボードの数値タイル。 */
export function StatCard({
  label,
  value,
  unit,
  hint,
  icon: Icon,
  tone = 'default',
  className,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  unit?: string;
  hint?: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  tone?: 'default' | 'success' | 'warning' | 'destructive' | 'info';
  className?: string;
}) {
  const toneClass = {
    default: 'text-foreground',
    success: 'text-success',
    warning: 'text-warning',
    destructive: 'text-destructive',
    info: 'text-info',
  }[tone];
  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-card p-4 shadow-lg shadow-black/10',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        {Icon ? <Icon className="size-4 text-muted-foreground" /> : null}
      </div>
      <p className={cn('mt-2 text-2xl font-semibold tabular', toneClass)}>
        {value}
        {unit ? <span className="ml-1 text-sm font-normal text-muted-foreground">{unit}</span> : null}
      </p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

/** 使用量バー。容量・クォータ・送信通数の消化率に使う。 */
export function UsageBar({
  used,
  total,
  label,
  formatValue,
  className,
}: {
  used: number;
  total: number;
  label?: React.ReactNode;
  formatValue?: (n: number) => string;
  className?: string;
}) {
  const ratio = total > 0 ? Math.min(used / total, 1) : 0;
  const tone = ratio >= 0.95 ? 'bg-destructive' : ratio >= 0.8 ? 'bg-warning' : 'bg-primary';
  // 既定フォーマッタは `formatCount()`（ロケールをこのファイルに持たない）。
  const fmt = formatValue ?? formatCount;
  return (
    <div className={cn('space-y-1', className)}>
      {label || total > 0 ? (
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">{label}</span>
          <span className="tabular text-muted-foreground">
            {fmt(used)} / {total > 0 ? fmt(total) : '無制限'}
          </span>
        </div>
      ) : null}
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full rounded-full transition-all', tone)}
          style={{ width: `${ratio * 100}%` }}
        />
      </div>
    </div>
  );
}

/** 定義リスト。詳細画面のメタ情報表示。 */
export function KeyValue({
  items,
  columns = 2,
  className,
}: {
  items: Array<{ key: React.ReactNode; value: React.ReactNode }>;
  columns?: 1 | 2 | 3;
  className?: string;
}) {
  const grid = { 1: 'sm:grid-cols-1', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3' }[columns];
  return (
    <dl className={cn('grid grid-cols-1 gap-x-6 gap-y-3', grid, className)}>
      {items.map((it, i) => (
        <div key={i} className="min-w-0 space-y-0.5">
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {it.key}
          </dt>
          <dd className="break-words text-sm text-foreground">{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** コピーできるコード片。DNS レコードや接続情報の提示に使う。 */
export function CopyableCode({
  value,
  label,
  className,
}: {
  value: string;
  label?: React.ReactNode;
  className?: string;
}) {
  const [copied, setCopied] = React.useState(false);
  const copy = React.useCallback(() => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  }, [value]);
  return (
    <div className={cn('space-y-1', className)}>
      {label ? (
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      ) : null}
      <div className="flex items-stretch gap-2">
        <code className="min-w-0 flex-1 overflow-x-auto whitespace-pre rounded-md border border-border bg-panel px-3 py-2 font-mono text-xs text-foreground">
          {value}
        </code>
        <button
          type="button"
          onClick={copy}
          className="shrink-0 rounded-md border border-border bg-panel-elevated px-3 text-xs font-medium transition-colors hover:bg-hover"
        >
          {copied ? 'コピーしました' : 'コピー'}
        </button>
      </div>
    </div>
  );
}
